import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Modal from '@material-ui/core/Modal';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import {
  RadioGroup,
  Radio,
  TableBody,
  FormLabel,
  ListItem,
  Select,
  Input,
  InputLabel,
  ListItemText
} from '@material-ui/core';
import RequiredButton from '../../components/atoms/RequiredButton';
import AnyButton from '../../components/atoms/AnyButton';
import CancelButton from '../../components/atoms/CancelButton';
import ConfirmButton from '../../components/atoms/ConfirmButton';
import FileUpload from '../../components/molecules/FileUpload';
import {
  getGochiMenus,
  editGochiMenu,
  addGochiMenuImage
} from '../../actions/GochiMenus';
import { resizeImage, uploadFile, sendLogin } from '../../auth/Auth';
import ValidationText from '../../components/atoms/ValidationText';
import listMenuChildTypes from '../../constants/MenuChildType';
import GochiMenuEditConfirmGroup from './GochiMenuEditConfirmGroup';
import { languages, languageType } from '../../constants/Language';
import enabledStripeBilling from '../../constants/StripeBilling';

const styles = theme => ({
  root: {
    position: 'absolute',
    width: '100%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '95%',
      padding: 16
    }
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      marginBottom: 10
    }
  },
  title: {
    fontWeight: 'bold',
    fontSize: 20
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 5px'
    },
    display: 'flex',
    flexDirection: 'column'
  },
  alignEnd: {
    alignSelf: 'end'
  },
  editTable: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    },
    '& tr': {
      [theme.breakpoints.down('sm')]: {
        display: 'block'
      }
    }
  },
  tableCellBorderNone: {
    borderBottom: 'none'
  },
  textField: {
    minWidth: '300px',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  priceCalc: {
    margin: '0px 5px',
    color: '#aaa'
  },
  colLeft: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      border: 0,
      paddingBottom: 5
    },
    verticalAlign: 'top',
    width: 44,
    minWidth: 44
  },
  colRight: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      border: 0,
      paddingTop: 0
    },
    verticalAlign: 'top'
  },
  hideMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  width100: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      display: 'block',
      padding: 0
    }
  },
  displayFlex: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      border: 0,
      paddingTop: 0
    }
  },
  displayBlock: {
    display: 'block'
  },
  textMutiple: {
    paddingTop: '46px',
    width: '33px'
  },
  textFieldPriceCombo: {
    padding: '0px 5px 0px 5px',
    width: '200px',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  selectContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  select: {
    width: '300px'
  },
  modal: {
    maxWidth: 978,
    margin: 'auto',
    padding: '5% 12px 5%'
  },
  description: {
    fontWeight: 500,
    fontSize: 12
  },
  marginTop: {
    marginTop: 15
  },
  situationPulldown: {
    maxWidth: '751px'
  },
  situationText: {
    fontSize: '0.75rem'
  },
  pulldownHeight: {
    maxHeight: '350px'
  },
  fukuokaTextPrice: {
    color: '#F50057',
    fontSize: '14px'
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      name: state.loginInfo.name,
      detail: state.gochiMenus.detail,
      shops: state.shops.shops,
      taxTypes: state.gochiMenus.taxTypes,
      reducedTaxMenuPrefix: state.commons.reducedTaxMenuPrefix
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getGochiMenus,
          editGochiMenu,
          addGochiMenuImage
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openEditProgress', 'updateOpenEditProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isEditSuccess', 'updateIsEditSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('menuChildType', 'setMenuChildType', 1),
  withState('comboMenu', 'setComboMenu', 0),
  withState('groupId', 'setGroupId', 0),
  withState('groupName', 'setGroupName', ''),
  withState('shopId', 'setShopId', 0),
  withState('shopName', 'setShopName', ''),
  withState('menuId', 'setMenuId', 0),
  withState('menuName', 'setMenuName', ''),
  withState('menuNameError', 'setMenuNameError', ''),
  withState('price', 'setPrice', ''),
  withState('priceError', 'setPriceError', ''),
  withState('priceCalc', 'setPriceCalc', '000 (税込み)'),
  withState('priceCombo', 'setPriceCombo', ''),
  withState('priceComboError', 'setPriceComboError', ''),
  withState('quantityRelease', 'setQuantityRelease', ''),
  withState('quantityReleaseError', 'setQuantityReleaseError', ''),
  withState('quantityPromotion', 'setQuantityPromotion', ''),
  withState('quantityPromotionError', 'setQuantityPromotionError', ''),
  withState('expiration', 'setExpiration', ''),
  withState('subscripIntervalCountError', 'setSubscripIntervalCountError', ''),
  withState('expirationError', 'setExpirationError', ''),
  withState('description', 'setDescription', ''),
  withState('taxType', 'setTaxType', 'INTERNAL'),
  withState('reducedTaxCheck', 'setReducedTaxCheck', false),
  withState('reducedTaxType', 'setReducedTaxType', 'UNREDUCED'),
  withState('tagStr', 'setTagStr', ''),
  withState('tags', 'setTags', null),
  withState('image', 'setImage', null),
  withState('previewImage', 'setPreviewImage', null),
  withState('updatedAt', 'setUpdatedAt', ''),
  withState('isLanguage', 'setIsLanguage', 'ja'),
  withState('translateType', 'setTranslateType', 0),
  withState('multiLangData', 'setMultiLangData', {}),
  withState('menuGenreId', 'setMenuGenreId', null),
  withState('subscriptionInterval', 'setSubscriptionInterval', 'WEEK'),
  withState('subscriptionIntervalCount', 'setSubscriptionIntervalCount', ''),
  withState('subscription', 'setSubscription', 0),
  withState('menuGenreName', 'setMenuGenreName', ''),
  withState('menuSituationIds', 'setmenuSituationIds', []),
  withState('menuSituationNames', 'setMenuSituationNames', ''),
  withState('menuUserTargetIds', 'setmenuUserTargetIds', []),
  withState('menuUserTargetNames', 'setMenuUserTargetNames', ''),
  withState('menuLimitTotal', 'setMenuLimitTotal', 0),
  withState('menuGenreError', 'setMenuGenreError', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    defaultPrice: props => () => {
      let includedTaxPrice = 0;
      let excludedTaxPrice = 0;
      // TODO DBから取得
      let tax = 0.1;
      if (props.editInput.reducedTaxCheck) {
        tax = 0.08;
      }

      if (props.editInput.price !== '') {
        if (props.editInput.taxType === 'INTERNAL') {
          const amountTemp = parseFloat(
            (props.editInput.price / (1 + tax)).toFixed(9)
          );
          const taxAmount = Math.round(amountTemp * tax);
          includedTaxPrice = props.editInput.price;
          excludedTaxPrice = props.editInput.price - taxAmount;
        } else if (props.editInput.taxType === 'DONATION') {
          includedTaxPrice = props.editInput.price;
          excludedTaxPrice = props.editInput.price;
        } else {
          includedTaxPrice =
            props.editInput.price + Math.round(props.editInput.price * tax);
          excludedTaxPrice = props.editInput.price;
        }
      }
      // props.setPriceCalc(`${includedTaxPrice} (税抜 ${excludedTaxPrice})`);
      console.log(excludedTaxPrice);
      const priceShow = includedTaxPrice
        ? parseInt(includedTaxPrice, 0).toString()
        : '0';
      props.setPriceCalc(`${priceShow} (税込み)`);
    }
  }),
  lifecycle({
    componentDidMount() {
      this.props.setMenuChildType(this.props.editInput.menuChildType);
      this.props.setGroupId(this.props.editInput.groupId);
      this.props.setGroupName(this.props.editInput.groupName);
      this.props.setShopId(this.props.editInput.shopId);
      this.props.setShopName(this.props.editInput.shopName);
      this.props.setMenuId(this.props.editInput.menuId);
      this.props.setMenuName(this.props.editInput.menuName);
      this.props.setPrice(this.props.editInput.price);
      this.props.setComboMenu(this.props.editInput.comboMenu);
      this.props.setSubscription(this.props.editInput.subscription);
      this.props.setSubscriptionIntervalCount(
        this.props.editInput.subscriptionIntervalCount
      );
      this.props.setSubscriptionInterval(
        this.props.editInput.subscriptionInterval
      );
      this.props.setMenuLimitTotal(this.props.editInput.menuLimitTotal || 0);
      this.props.setPriceCombo(this.props.editInput.priceCombo);
      this.props.setQuantityRelease(this.props.editInput.quantityRelease);
      this.props.setQuantityPromotion(this.props.editInput.quantityPromotion);
      this.props.setExpiration(this.props.editInput.expiration);
      this.props.setDescription(this.props.editInput.description);
      this.props.setTaxType(this.props.editInput.taxType);
      this.props.setReducedTaxType(this.props.editInput.reducedTaxType);
      if (this.props.editInput.reducedTaxType === 'REDUCED') {
        this.props.setReducedTaxCheck(true);
      }
      this.props.setTags(this.props.editInput.tags);
      this.props.setTagStr(this.props.editInput.tagStr);
      this.props.setPreviewImage(this.props.editInput.image);
      this.props.setUpdatedAt(this.props.editInput.updatedAt);
      this.props.defaultPrice();
      if (this.props.editInput.menuGenreId) {
        this.props.setMenuGenreId(this.props.editInput.menuGenreId.toString());
      } else {
        this.props.setMenuGenreId(null);
      }

      if (
        this.props.editInput.menuSituationIds &&
        this.props.editInput.menuSituationIds.length
      ) {
        this.props.setmenuSituationIds(
          this.props.editInput.menuSituationIds.map(String)
        );
      } else {
        this.props.setmenuSituationIds([]);
      }

      if (
        this.props.editInput.menuUserTargetIds &&
        this.props.editInput.menuUserTargetIds.length
      ) {
        this.props.setmenuUserTargetIds(
          this.props.editInput.menuUserTargetIds.map(String)
        );
      } else {
        this.props.setmenuUserTargetIds([]);
      }

      let dataFirst = {};
      const origilData = {
        shopIdAdd: null,
        menuChildType: null,
        comboMenu: null,
        menuLimitTotal: null,
        groupId: null,
        groups: null,
        shops: null,
        subscription: null,
        subscriptionIntervalCount: null,
        subscriptionInterval: null,
        shopName: null,
        menuName: null,
        price: null,
        priceCalc: null,
        priceCombo: null,
        quantityRelease: null,
        quantityPromotion: null,
        promotionDescription: null,
        usageLimit: null,
        expirationType: null,
        expiration: null,
        expirationEndDate: null,
        minExpirationDate: null,
        maxExpirationDate: null,
        expirationList: null,
        quantityReleaseList: null,
        description: null,
        taxType: null,
        reducedTaxCheck: null,
        reducedTaxType: null,
        tagStr: null,
        tags: null,
        updatedAt: null,
        menuGenreId: null,
        menuGenreName: null,
        menuSituationIds: [],
        menuSituationNames: null,
        menuUserTargetIds: [],
        menuUserTargetNames: null
      };
      dataFirst = origilData;
      if (this.props.editInput.otherLanguages) {
        const str = JSON.stringify(this.props.editInput.otherLanguages);
        dataFirst.otherLanguages = JSON.parse(str);
      }
      languages.forEach(item => {
        if (item.value === languageType.ORIGIN) return;
        const translatedData = dataFirst.otherLanguages.find(
          el => el.language === item.value
        );
        if (!translatedData) {
          const other = {
            translateType: 0,
            language: item.value
          };
          dataFirst.otherLanguages.push(other);
        }
      });
      this.props.setMultiLangData(dataFirst);
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    searchKey,
    roles,
    editInput,
    openEditConfirm,
    updateOpenEdit,
    updateOpenEditConfirm,
    openEditProgress,
    updateOpenEditProgress,
    loading,
    updateLoading,
    isEditSuccess,
    updateIsEditSuccess,
    resultMessage,
    setResultMessage,
    menuChildType,
    groupId,
    shopId,
    shopName,
    menuId,
    menuName,
    setMenuName,
    subscripIntervalCountError,
    setSubscripIntervalCountError,
    comboMenu,
    menuLimitTotal,
    // setComboMenu,
    price,
    setPrice,
    priceCalc,
    setPriceCalc,
    priceCombo,
    setPriceCombo,
    priceComboError,
    setPriceComboError,
    quantityRelease,
    setQuantityRelease,
    quantityReleaseError,
    setQuantityReleaseError,
    quantityPromotion,
    setQuantityPromotion,
    quantityPromotionError,
    setQuantityPromotionError,
    usageLimit,
    description,
    setDescription,
    promotionDescription,
    expiration,
    setExpiration,
    expirationError,
    setExpirationError,
    expirationEndDate,
    taxType,
    setTaxType,
    taxTypes,
    reducedTaxCheck,
    setReducedTaxCheck,
    reducedTaxType,
    setReducedTaxType,
    tagStr,
    setTagStr,
    tags,
    setTags,
    image,
    setImage,
    previewImage,
    setPreviewImage,
    updatedAt,
    reducedTaxMenuPrefix,
    menuNameError,
    setMenuNameError,
    priceError,
    setPriceError,
    setMenuChildType,
    isLanguage,
    setIsLanguage,
    multiLangData,
    setMultiLangData,
    translateType,
    setTranslateType,
    menuGenreId,
    setMenuGenreId,
    menuGenreName,
    setMenuGenreName,
    menuSituationIds,
    setmenuSituationIds,
    menuSituationNames,
    setMenuSituationNames,
    menuUserTargetIds,
    setmenuUserTargetIds,
    menuUserTargetNames,
    setMenuUserTargetNames,
    subscriptionInterval,
    setSubscriptionInterval,
    subscriptionIntervalCount,
    setSubscriptionIntervalCount,
    subscription,
    setSubscription,
    menuGenre,
    menuSituation,
    menuUserTarget,
    menuGenreError,
    setMenuGenreError,
    isFukuoka
  }) => {
    const errors = {
      menuNameError,
      priceError,
      priceComboError,
      quantityReleaseError,
      subscripIntervalCountError,
      quantityPromotionError,
      expirationError,
      menuGenreError
    };

    const validMenuName = () => {
      if (isLanguage !== languageType.ORIGIN && translateType === 0) {
        return 0;
      }
      const value = menuName;
      if (value.trim() === '') {
        setMenuNameError('必須項目です。');
        return 1;
      }
      setMenuNameError('');
      setMenuName(value);
      return 0;
    };
    const validMenuGenre = () => {
      const value = menuGenreId;
      if (!value) {
        setMenuGenreError('必須項目です。');
        return 1;
      }
      setMenuGenreError('');
      setMenuGenreId(value);
      return 0;
    };
    const validPrice = () => {
      const value = price;
      if (value === '') {
        setPriceError('必須項目です。');
        return 1;
      }
      if (!/^\d+$/.test(value)) {
        setPriceError('半角数字のみ入力してください。');
        return 1;
      }
      setPriceError('');
      setPrice(value);
      return 0;
    };
    const validPriceCombo = () => {
      const value = priceCombo;
      if (value === '') {
        setPriceComboError('必須項目です。');
        return 1;
      }
      if (!/^\d+$/.test(value)) {
        setPriceComboError('半角数字のみ入力してください。');
        return 1;
      }
      setPriceComboError('');
      setPriceCombo(parseInt(value, 10));
      return 0;
    };
    const validQuantityRelease = () => {
      const value = quantityRelease;
      if (value === '') {
        setQuantityReleaseError('必須項目です。');
        return 1;
      }
      if (!/^[1-9]|[1-9]\d+$/.test(value)) {
        setQuantityReleaseError('半角数字のみ入力してください。');
        return 1;
      }
      setQuantityReleaseError('');
      setQuantityRelease(parseInt(value, 10));
      return 0;
    };
    const validSubscriptionIntervalCount = () => {
      const checkSubscription = subscription;
      if (checkSubscription === 1) {
        const valueIntervalCount = subscriptionIntervalCount;
        const valueInterval = subscriptionInterval;
        if (valueIntervalCount === '') {
          setSubscripIntervalCountError('必須項目です。');
          return 1;
        }
        if (Number(valueIntervalCount) < 1) {
          setSubscripIntervalCountError('頻度価値は1以上ご入力ください');
          return 1;
        }
        if (!/^\d+$/.test(valueIntervalCount)) {
          setSubscripIntervalCountError('半角数字のみ入力してください。');
          return 1;
        }
        if (valueInterval === 'WEEK' && Number(valueIntervalCount) > 52) {
          setSubscripIntervalCountError(
            '頻度価値は週間サブスクに対して52以下、月間サブスクに対して12以下入力してください。'
          );
          return 1;
        }
        if (valueInterval === 'MONTH' && Number(valueIntervalCount) > 12) {
          setSubscripIntervalCountError(
            '頻度価値は週間サブスクに対して52以下、月間サブスクに対して12以下入力してください。'
          );
          return 1;
        }
      }
      setSubscripIntervalCountError('');
      return 0;
    };
    const validQuantityPromotion = () => {
      const value = quantityPromotion;
      if (value === '' || !value) {
        setQuantityPromotionError('');
        return 0;
      }
      if (!/^\d+$/.test(value)) {
        setQuantityPromotionError('半角数字のみ入力してください。');
        return 1;
      }
      setQuantityPromotionError('');
      setQuantityPromotion(parseInt(value, 10));
      return 0;
    };
    const validExpiration = () => {
      const value = expiration;
      if (value === '' || !value) {
        setExpirationError('');
        return 0;
      }
      if (!/\b([1-9]|[1-9][0-9]|1[0-7][0-9]|180)\b/.test(value)) {
        setExpirationError('半角数字のみ入力してください。From 1 - 180');
        return 1;
      }
      setExpirationError('');
      setExpiration(parseInt(value, 10));
      return 0;
    };

    const valid = () => {
      let errorCount = 0;
      // Menu normal
      if (comboMenu === 0) {
        errorCount =
          validMenuName() +
          validSubscriptionIntervalCount() +
          validPrice() +
          validMenuGenre();
      } else if (comboMenu === 1) {
        // Menu combo
        errorCount =
          validMenuName() +
          // validPrice() +
          validPriceCombo() +
          validQuantityRelease() +
          validSubscriptionIntervalCount() +
          validQuantityPromotion() +
          validExpiration() +
          validMenuGenre();
      }

      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const getCurrentData = () => {
      const lang = isLanguage;
      let data = multiLangData;
      const origilParam = {
        menuChildType,
        groupId,
        shopId,
        shopName,
        menuName,
        price,
        priceCalc,
        description,
        subscription,
        subscriptionIntervalCount:
          subscription === 1 ? subscriptionIntervalCount : undefined,
        subscriptionInterval:
          subscription === 1 ? subscriptionInterval : undefined,
        menuCombo: comboMenu,
        menuLimitTotal:
          menuChildType !== 3 && comboMenu === 0 ? menuLimitTotal : undefined,
        priceCombo,
        quantity: quantityRelease,
        quantityFree: quantityPromotion,
        quantityFreeDes: promotionDescription,
        numberUsePerDay: usageLimit,
        // expirationList,
        // quantityReleaseList,
        expiration,
        expirationEndDate,
        taxType,
        reducedTaxType,
        tags,
        tagStr,
        updatedAt,
        menuGenreId,
        menuSituationIds,
        menuUserTargetIds
      };
      const otherParam = {
        translateType,
        language: lang,
        params: {
          menuName,
          description,
          quantityFreeDes: promotionDescription
        }
      };
      // origin
      if (lang === languageType.ORIGIN) {
        data = { ...data, ...origilParam };
      } else {
        // other languages
        for (let index = 0; index < data.otherLanguages.length; index += 1) {
          if (data.otherLanguages[index].language === lang) {
            data.otherLanguages[index] = otherParam;
          }
        }
      }
      const str = JSON.stringify(data);
      return JSON.parse(str);
    };

    const getGenreName = () => {
      if (menuGenreId) {
        const genreName = menuGenre.find(
          genre => genre.menuGenreId.toString() === menuGenreId.toString()
        ).menuGenreName;
        return genreName;
      }
      return '';
    };

    const getSituationName = () => {
      const situationName = [];
      if (menuSituationIds.length) {
        menuSituationIds.forEach(id => {
          const name = menuSituation.find(
            situation => situation.menuSituationId.toString() === id.toString()
          ).menuSituationName;
          situationName.push(name);
        });
        return situationName.join(', ');
      }
      return '';
    };

    const getUserTargetName = () => {
      const userTargetName = [];
      if (menuUserTargetIds.length) {
        menuUserTargetIds.forEach(id => {
          const names = menuUserTarget.find(
            userTarget =>
              userTarget.menuUserTargetId.toString() === id.toString()
          ).name;
          userTargetName.push(names);
        });
        return userTargetName.join(', ');
      }
      return '';
    };

    const cancelAction = () => updateOpenEdit(false);
    const confirmAction = () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        setMenuGenreName(getGenreName());
        setMenuSituationNames(getSituationName());
        setMenuUserTargetNames(getUserTargetName());
        setMultiLangData(getCurrentData());
        updateOpenEditConfirm(true);
      }
    };
    const backAction = () => updateOpenEditConfirm(false);
    const editAction = async () => {
      updateLoading(true);
      updateOpenEditProgress(true);
      const editedMenu = await actions
        .editGochiMenu(menuId, multiLangData)
        .then(data => checkSession(data));
      if (typeof image !== 'undefined' && image !== null) {
        let imageData = image;
        if (imageData.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
          const { resizeImageFile } = await resizeImage(imageData);
          imageData = resizeImageFile;
        }
        const imagePath = await uploadFile('menu', menuId, imageData, 'public');
        await actions
          .addGochiMenuImage(menuId, {
            image: imagePath
          })
          .then(data => checkSession(data));
      }
      if (editedMenu.type === 'EDIT_GOCHI_MENU_SUCCESS') {
        updateIsEditSuccess(true);
        setResultMessage('編集が完了しました。');
        updateLoading(false);
      } else {
        updateIsEditSuccess(false);
        setResultMessage('編集に失敗しました。');
        updateLoading(false);
      }
    };
    const progressOkAction = () => {
      if (isEditSuccess) {
        actions.getGochiMenus(searchKey).then(data => checkSession(data));
        updateOpenEdit(false);
        updateOpenEditConfirm(false);
        updateOpenEditProgress(false);
      } else {
        updateOpenEditProgress(false);
      }
    };

    const calcPrice = e => {
      let includedTaxPrice = 0;
      let excludedTaxPrice = 0;

      let latestPrice = price;
      if (e.target.id === 'price') {
        latestPrice = e.target.value;
      }

      let latestTaxType = taxType;
      if (e.target.name === 'taxType') {
        latestTaxType = e.target.value;
      }

      let lastestPriceCombo = priceCombo;
      if (e.target.id === 'priceCombo') {
        lastestPriceCombo = e.target.value;
      }

      let lastestQuantityRelease = quantityRelease;
      if (e.target.name === 'quantityRelease') {
        lastestQuantityRelease = e.target.value;
      }

      if (
        lastestPriceCombo &&
        lastestPriceCombo !== '' &&
        lastestQuantityRelease &&
        lastestQuantityRelease !== ''
      ) {
        setPrice(lastestPriceCombo * lastestQuantityRelease);
        latestPrice = lastestPriceCombo * lastestQuantityRelease;
      }
      if (
        (!lastestPriceCombo ||
          lastestPriceCombo === '' ||
          !lastestQuantityRelease ||
          lastestQuantityRelease === '') &&
        comboMenu === 1
      ) {
        setPrice(lastestPriceCombo * lastestQuantityRelease);
        setPriceCalc('000 (税込み)');
        latestPrice = lastestPriceCombo * lastestQuantityRelease;
      }

      // TODO DBから取得
      let tax = 0.1;
      if (
        (e.target.id !== 'reducedTax' && reducedTaxCheck) ||
        (e.target.id === 'reducedTax' && e.target.checked)
      ) {
        // TODO DBから取得
        tax = 0.08;
      }

      if (latestPrice !== '') {
        if (latestTaxType === 'INTERNAL') {
          const amountTemp = parseFloat((latestPrice / (1 + tax)).toFixed(9));
          const taxAmount = Math.round(amountTemp * tax);
          includedTaxPrice = latestPrice;
          excludedTaxPrice = latestPrice - taxAmount;
        } else if (latestTaxType === 'DONATION') {
          includedTaxPrice = latestPrice;
          excludedTaxPrice = latestPrice;
        } else {
          const taxAmount = Math.round(latestPrice * tax);
          includedTaxPrice = latestPrice * 1 + taxAmount;
          excludedTaxPrice = latestPrice;
        }
      }

      // setPriceCalc(`${includedTaxPrice} (税抜 ${excludedTaxPrice})`);
      console.log(excludedTaxPrice);
      const val =
        e.target.id !== 'reducedTax' && typeof e.target.id !== 'undefined'
          ? e.target.value
          : includedTaxPrice;
      console.log('val', val);
      const priceShow =
        val !== '' ? parseInt(includedTaxPrice, 0).toString() : '000';
      setPriceCalc(`${priceShow} (税込み)`);
    };
    const splitTagStr = str => {
      if (str === '') {
        setTags(null);
      } else {
        setTags(
          str.split(/\s+/).map(t => {
            return { tagName: t };
          })
        );
      }
    };
    const convertReducedTax = checked => {
      if (checked) {
        setReducedTaxType('REDUCED');
      } else {
        setReducedTaxType('UNREDUCED');
      }
    };

    // setting language
    const setLanguage = v => {
      const lang = isLanguage;
      const data = getCurrentData();
      if (
        (v !== languageType.ORIGIN && lang === languageType.ORIGIN) ||
        (v !== languageType.ORIGIN && lang !== languageType.ORIGIN)
      ) {
        // origin to other or other to other lang
        for (let k = 0; k < multiLangData.otherLanguages.length; k += 1) {
          if (multiLangData.otherLanguages[k].language === v) {
            setMenuName(
              multiLangData.otherLanguages[k].params &&
                multiLangData.otherLanguages[k].params.menuName
                ? multiLangData.otherLanguages[k].params.menuName
                : ''
            );
            setDescription(
              multiLangData.otherLanguages[k].params &&
                multiLangData.otherLanguages[k].params.description
                ? multiLangData.otherLanguages[k].params.description
                : ''
            );
            setTranslateType(
              multiLangData.otherLanguages[k].translateType
                ? multiLangData.otherLanguages[k].translateType
                : 0
            );
          }
        }
      } else if (v === languageType.ORIGIN) {
        // other to origin
        setMenuName(multiLangData.menuName);
        setDescription(multiLangData.description);
      }
      setIsLanguage(v);
      setMultiLangData(data);
    };
    return (
      <div className={classes.root}>
        <div className={classes.pageTitle}>
          <Typography className={classes.title} variant="h6">
            {isFukuoka ? 'コース編集' : 'メニュー編集'}
          </Typography>
        </div>
        <div className={classes.selectContainer}>
          <Select
            className={`${classes.textField} ${classes.select}`}
            value={isLanguage}
            onChange={e => {
              if (isLanguage === e.target.value) {
                return;
              }
              if (
                valid() ||
                (isLanguage !== languageType.ORIGIN && translateType === 0)
              ) {
                setLanguage(e.target.value);
              } else {
                alert('登録情報を正しく入力してください。');
              }
            }}
          >
            {languages.map(lang => (
              <MenuItem key={lang.value} value={lang.value}>
                {lang.label}
              </MenuItem>
            ))}
          </Select>
        </div>

        {isLanguage !== languageType.ORIGIN && (
          <ListItem className={classes.itemForm}>
            <Typography component="div" className={classes.formInput}>
              <RadioGroup
                value={translateType}
                onChange={e => {
                  setTranslateType(parseInt(e.target.value, 10));
                }}
              >
                <FormControlLabel
                  value={0}
                  control={<Radio color="primary" />}
                  label="GG翻訳を選択"
                />
                <FormControlLabel
                  value={1}
                  control={<Radio color="primary" />}
                  label="自分で文章入力"
                />
              </RadioGroup>
            </Typography>
          </ListItem>
        )}
        <Table className={classes.editTable}>
          <TableBody>
            {(roles.isSecretariat ||
              roles.isChildShop ||
              roles.isGroup ||
              roles.isChildGroup) && (
              <>
                {parseInt(editInput.menuChildType, 10) !== 3 &&
                  parseInt(editInput.menuChildType, 10) !== 4 &&
                  isLanguage === languageType.ORIGIN && (
                    <TableRow>
                      <TableCell className={classes.colLeft}>
                        <RequiredButton />
                      </TableCell>
                      <TableCell className={classes.colRight}>
                        <TextField
                          required
                          fullWidth
                          label={isFukuoka ? '教室名' : '店舗名'}
                          defaultValue={editInput.shopName}
                          className={classes.textField}
                          InputProps={{
                            readOnly: true
                          }}
                          variant="filled"
                          InputLabelProps={{
                            shrink: true
                          }}
                        />
                      </TableCell>
                      <TableCell className={classes.width100} />
                    </TableRow>
                  )}
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <RequiredButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <TextField
                      required
                      fullWidth
                      label={isFukuoka ? 'コース名' : 'メニュー名'}
                      defaultValue={editInput.menuName}
                      inputProps={{
                        maxLength:
                          isLanguage === languageType.ORIGIN ? 250 : 1000
                      }}
                      value={menuName}
                      disabled={
                        isLanguage !== languageType.ORIGIN &&
                        translateType === 0
                      }
                      className={classes.textField}
                      onChange={e => {
                        setMenuName(e.target.value);
                      }}
                      onBlur={validMenuName}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                    {(isLanguage === languageType.ORIGIN ||
                      translateType === 1) && (
                      <ValidationText message={errors.menuNameError} />
                    )}
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
                {isLanguage === languageType.ORIGIN && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <FormLabel className={classes.description} required>
                        {isFukuoka ? 'コース種別' : '種別'}
                      </FormLabel>
                      <RadioGroup
                        value={menuChildType}
                        onChange={e => {
                          setMenuChildType(parseInt(e.target.value, 10));
                        }}
                      >
                        {isFukuoka
                          ? listMenuChildTypes
                              .filter(item => {
                                return item.id === 1 || item.id === 4;
                              })
                              .map(item => {
                                let nameRadio = '';
                                if (item.id === 1) {
                                  nameRadio = '通常（教室別）';
                                } else if (item.id === 4) {
                                  nameRadio = 'グループ共通';
                                }
                                return (
                                  <FormControlLabel
                                    disabled
                                    value={item.id}
                                    label={nameRadio}
                                    control={<Radio color="primary" />}
                                  />
                                );
                              })
                          : listMenuChildTypes
                              .filter(item => {
                                return item.id !== -1;
                              })
                              .map(item => (
                                <FormControlLabel
                                  disabled
                                  value={item.id}
                                  label={item.name}
                                  control={<Radio color="primary" />}
                                />
                              ))}
                      </RadioGroup>
                    </TableCell>
                  </TableRow>
                )}
                {editInput.menuChildType !== 3 &&
                  editInput.comboMenu === 0 &&
                  isLanguage === languageType.ORIGIN &&
                  !isFukuoka && (
                    <TableRow>
                      <TableCell className={classes.colLeft}>
                        <RequiredButton />
                      </TableCell>
                      <TableCell className={classes.colRight}>
                        <Typography className={classes.divisionLabel}>
                          利用方法
                        </Typography>
                        <RadioGroup row value={menuLimitTotal}>
                          <FormControlLabel
                            disabled
                            value={0}
                            control={<Radio color="primary" />}
                            label="一枚単位利用"
                          />
                          <FormControlLabel
                            disabled
                            value={1}
                            control={<Radio color="primary" />}
                            label="一円単位利用"
                          />
                        </RadioGroup>
                      </TableCell>
                      <TableCell className={classes.width100} />
                    </TableRow>
                  )}
                {enabledStripeBilling === true && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <RadioGroup
                        row
                        value={subscription}
                        onChange={e => {
                          setSubscription(parseInt(e.target.value, 10));
                        }}
                      >
                        <FormControlLabel
                          disabled
                          value={1}
                          control={<Radio color="primary" />}
                          label="利用する"
                        />
                        <FormControlLabel
                          disabled
                          value={0}
                          control={<Radio color="primary" />}
                          label="利用しない"
                        />
                      </RadioGroup>
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
                {subscription === 1 && enabledStripeBilling === true && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <Typography className={classes.description}>
                        購入頻度設定
                      </Typography>
                      <div className={classes.displayFlex}>
                        <div className={classes.displayBlock}>
                          <TextField
                            id="subscriptionIntervalCount"
                            required
                            disabled
                            label="頻度"
                            className={classes.textFieldPriceCombo}
                            margin="normal"
                            value={subscriptionIntervalCount}
                            onChange={e => {
                              setSubscriptionIntervalCount(
                                e.target.value.trim()
                              );
                            }}
                            onBlur={validSubscriptionIntervalCount}
                          />
                          <ValidationText
                            message={errors.subscripIntervalCountError}
                          />
                        </div>
                        <div className={classes.displayBlock}>
                          <TextField
                            id="subscriptionInterval"
                            name="subscriptionInterval"
                            select
                            required
                            disabled
                            label="単位"
                            className={classes.textFieldPriceCombo}
                            margin="normal"
                            value={subscriptionInterval}
                            InputProps={{
                              shrink: true
                            }}
                            onChange={e => {
                              setSubscriptionInterval(e.target.value.trim());
                            }}
                          >
                            <MenuItem key="0" value="WEEK">
                              週間
                            </MenuItem>
                            <MenuItem key="1" value="MONTH">
                              ヶ月
                            </MenuItem>
                          </TextField>
                        </div>
                        <div className={classes.textMutiple}>1回</div>
                      </div>
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
                {isLanguage === languageType.ORIGIN && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <TextField
                        id="price"
                        fullWidth
                        disabled={editInput.comboMenu === 1}
                        required
                        label={isFukuoka ? '金額' : '価格'}
                        defaultValue={editInput.price}
                        className={classes.textField}
                        value={price}
                        onChange={e => {
                          setPrice(e.target.value.trim());
                          calcPrice(e);
                        }}
                        onBlur={validPrice}
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                      <ValidationText message={errors.priceError} />
                      <div className={classes.priceCalc}>{priceCalc}</div>
                      {isFukuoka && (
                        <div className={classes.fukuokaTextPrice}>
                          ※10,000円以内の金額を入力してください
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
                {isLanguage === languageType.ORIGIN && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <div>
                        <TextField
                          id="taxType"
                          fullWidth
                          name="taxType"
                          select
                          label={isFukuoka ? '税区分' : '税区分・寄付'}
                          className={classes.textField}
                          value={taxType}
                          onChange={e => {
                            setTaxType(e.target.value.trim());
                            calcPrice(e);
                          }}
                          InputLabelProps={{
                            shrink: true
                          }}
                        >
                          {isFukuoka
                            ? taxTypes
                                .filter(group => {
                                  return group.id !== 'DONATION';
                                })
                                .map(group => {
                                  return (
                                    <MenuItem key={group.id} value={group.id}>
                                      {group.name}
                                    </MenuItem>
                                  );
                                })
                            : taxTypes.map(group => {
                                return (
                                  <MenuItem key={group.id} value={group.id}>
                                    {group.name}
                                  </MenuItem>
                                );
                              })}
                        </TextField>
                      </div>
                      {!isFukuoka && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              id="reducedTax"
                              checked={reducedTaxCheck}
                            />
                          }
                          label="軽減税率対象"
                          onChange={e => {
                            setReducedTaxCheck(e.target.checked);
                            convertReducedTax(e.target.checked);
                            calcPrice(e);
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
                {isLanguage === languageType.ORIGIN && !isFukuoka && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <TextField
                        required
                        select
                        label="ジャンル"
                        className={classes.textField}
                        margin="normal"
                        value={menuGenreId}
                        onChange={e => {
                          setMenuGenreId(e.target.value);
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                        onBlur={validMenuGenre}
                      >
                        {menuGenre &&
                          menuGenre.map(item => {
                            return (
                              <MenuItem
                                key={item.menuGenreId}
                                value={item.menuGenreId}
                              >
                                {item.menuGenreName}
                              </MenuItem>
                            );
                          })}
                      </TextField>
                      <ValidationText message={errors.menuGenreError} />
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
                {isLanguage === languageType.ORIGIN && !isFukuoka && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <AnyButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <InputLabel
                        id="situation-label"
                        className={classes.situationText}
                      >
                        シチュエーション
                      </InputLabel>
                      <Select
                        labelId="situation-label"
                        multiple
                        value={menuSituationIds}
                        onChange={e => setmenuSituationIds(e.target.value)}
                        input={<Input />}
                        renderValue={() => getSituationName()}
                        className={classes.situationPulldown}
                        MenuProps={{
                          classes: { paper: classes.pulldownHeight },
                          getContentAnchorEl: null
                        }}
                        fullWidth
                      >
                        {menuSituation &&
                          menuSituation.map(situation => (
                            <MenuItem
                              key={situation.menuSituationId}
                              value={situation.menuSituationId}
                            >
                              <Checkbox
                                checked={
                                  menuSituationIds.indexOf(
                                    situation.menuSituationId
                                  ) > -1
                                }
                              />
                              <ListItemText
                                primary={situation.menuSituationName}
                              />
                            </MenuItem>
                          ))}
                      </Select>
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
                {isLanguage === languageType.ORIGIN && !isFukuoka && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <AnyButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <InputLabel
                        id="user-target-label"
                        className={classes.situationText}
                      >
                        贈り相手
                      </InputLabel>
                      <Select
                        labelId="user-target-label"
                        multiple
                        value={menuUserTargetIds}
                        onChange={e => setmenuUserTargetIds(e.target.value)}
                        input={<Input />}
                        renderValue={() => getUserTargetName()}
                        className={classes.situationPulldown}
                        MenuProps={{
                          classes: { paper: classes.pulldownHeight },
                          getContentAnchorEl: null
                        }}
                        fullWidth
                      >
                        {menuUserTarget &&
                          menuUserTarget.map(target => (
                            <MenuItem
                              key={target.menuUserTargetId}
                              value={target.menuUserTargetId}
                            >
                              <Checkbox
                                checked={
                                  menuUserTargetIds.indexOf(
                                    target.menuUserTargetId
                                  ) > -1
                                }
                              />
                              <ListItemText primary={target.name} />
                            </MenuItem>
                          ))}
                      </Select>
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <AnyButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <TextField
                      label="説明"
                      multiline
                      fullWidth
                      rows="4"
                      defaultValue={editInput.description}
                      onChange={e => {
                        setDescription(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                      value={description}
                      disabled={
                        isLanguage !== languageType.ORIGIN &&
                        translateType === 0
                      }
                      inputProps={{
                        maxLength:
                          isLanguage === languageType.ORIGIN ? 250 : 1000
                      }}
                    />
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
                {isLanguage === languageType.ORIGIN && !isFukuoka && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <AnyButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <TextField
                        label="タグ"
                        defaultValue={editInput.tagStr}
                        onChange={e => {
                          setTagStr(e.target.value);
                          splitTagStr(e.target.value);
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                        fullWidth
                      />
                      <div
                        className={`${classes.description} ${
                          classes.marginTop
                        }`}
                      >
                        #おすすめ #おふくろの味 #学生に ＃ガッツリ
                        などメニューの特徴を表すタグを記載できます。
                      </div>
                    </TableCell>
                    <TableCell className={classes.colRight} />
                  </TableRow>
                )}
                {isLanguage === languageType.ORIGIN && (
                  <TableRow>
                    <TableCell
                      className={[classes.tableCellBorderNone, classes.colLeft]}
                    >
                      <AnyButton />
                    </TableCell>
                    <TableCell
                      className={[
                        classes.tableCellBorderNone,
                        classes.colRight
                      ]}
                    >
                      <FormLabel className={classes.description}>
                        種別
                      </FormLabel>
                      <FileUpload
                        label="image"
                        file={image}
                        setFile={setImage}
                        previewImage={previewImage}
                        setPreviewImage={setPreviewImage}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCellBorderNone} />
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
        <div>
          <CancelButton onClickAction={cancelAction} color="primary" />
          <ConfirmButton onClickAction={confirmAction} />
        </div>
        <Modal open={openEditConfirm} className={classes.modal}>
          <GochiMenuEditConfirmGroup
            backAction={backAction}
            editAction={editAction}
            editConfirm={multiLangData}
            roles={roles}
            reducedTaxMenuPrefix={reducedTaxMenuPrefix}
            openEditProgress={openEditProgress}
            progressOkAction={progressOkAction}
            loading={loading}
            resultMessage={resultMessage}
            currentLanguage={isLanguage}
            setIsLanguage={setIsLanguage}
            previewImage={previewImage}
            menuId={menuId}
            menuGenreName={menuGenreName}
            menuSituationNames={menuSituationNames}
            menuUserTargetNames={menuUserTargetNames}
            isFukuoka={isFukuoka}
          />
        </Modal>
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
